import {UploadOptionsEntity} from "@dropDesk/domain/entities/ticket/message/upload_message.entity";
import {
    AudioMessageEntity, ContactMessageEntity,
    DocumentMessageEntity,
    ImageMessageEntity, LocationMessageEntity,
    MessageChatType,
    MessageStatus, ReactionMessageEntity, ReplyEntity, StickerMessageEntity, SystemMessageEntity,
    TextMessageEntity, VideoMessageEntity
} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import {SimpleUserInfo, UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {MessageBoxStatus, MessagePosition, StatusMessageError} from "@dropDesk/domain/entities/messages/message_enums";
import {MessageMediaBase} from "@dropDesk/domain/entities/ticket/message/external/message_media_base";
import {UserRole} from "@dropDesk/domain/entities/user/user_enum";
import {Observable, Observer} from "rxjs";
import {RemoteStorageDatasource} from "@dropDesk/data/data_source/remote_storage/remote_storage_datasource";
import firebase from "firebase/compat";
import FirebaseStorageError = firebase.storage.FirebaseStorageError;
import {TicketMessageEntity} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";

export abstract class BaseMessageEntity {
    id!: string;
    idCompany!: string;
    idExternal?: string;
    type!: MessageChatType;
    senderId!: string;
    sender?: UserEntity;
    deleted!: boolean;
    edited?: boolean;
    isPrivate!: boolean;
    users?: Record<string, unknown>;
    received?: Record<string, unknown>;
    readers?: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
    fromMe!: boolean;
    participantId?: string | null;
    pushName?: string | null;
    status!: MessageStatus;
    textMessage?: TextMessageEntity | null;
    imageMessage?: ImageMessageEntity | null;
    videoMessage?: VideoMessageEntity | null;
    documentMessage?: DocumentMessageEntity | null;
    locationMessage?: LocationMessageEntity | null;
    stickerMessage?: StickerMessageEntity | null;
    audioMessage?: AudioMessageEntity | null;
    reactionMessage?: ReactionMessageEntity | null;
    contactMessage?: ContactMessageEntity[] | null;
    systemMessage?: SystemMessageEntity | null;
    reply?: ReplyEntity | null;
    fromSystem?: boolean;
    outOfBusinessHour?: boolean;
    position?: MessagePosition;
    automatic?: boolean;
    statusError?: StatusMessageError | null;

    uploadOptions?: UploadOptionsEntity;
    createdAtLocal!: string;
    blobURL?: string | null;
    showOriginal?: boolean;
    loaded?: boolean;


    constructor(data: {
        id: string;
        idCompany: string;
        idExternal?: string;
        type: MessageChatType;
        senderId: string;
        sender?: UserEntity;
        deleted?: boolean;
        edited?: boolean;
        isPrivate?: boolean;
        users?: Record<string, unknown>;
        received?: Record<string, unknown>;
        readers?: Record<string, unknown>;
        createdAt?: string;
        updatedAt?: string;
        fromMe: boolean;
        participantId?: string | null;
        pushName?: string | null;
        status: MessageStatus;
        textMessage?: TextMessageEntity | null;
        imageMessage?: ImageMessageEntity | null;
        videoMessage?: VideoMessageEntity | null;
        documentMessage?: DocumentMessageEntity | null;
        locationMessage?: LocationMessageEntity | null;
        stickerMessage?: StickerMessageEntity | null;
        audioMessage?: AudioMessageEntity | null;
        reactionMessage?: ReactionMessageEntity | null;
        contactMessage?: ContactMessageEntity[] | null;
        reply?: ReplyEntity | null;
        fromSystem?: boolean;
        outOfBusinessHour?: boolean;
        position?: MessagePosition;
        automatic?: boolean;
        statusError?: StatusMessageError | null;
        uploadOptions?: UploadOptionsEntity;
        blobURL?: string | null;
        createdAtLocal?: string;
        showOriginal?: boolean;
        loaded?: boolean;
        systemMessage?: SystemMessageEntity | null;
    }) {
        Object.assign(this, data);
    }

    public isUnsaved(): boolean {
        return !this.createdAt;
    }

    public get socialMessage(): string {

        if (this.deleted) {
            return "Mensagem apagada";
        }

        if (this.isLargeMediaError) {
            return this.getMessageLargeMediaError;
        }

        if (this.isViewOnceError) {
            return this.getMessageViewOnceError;
        }

        if (this.isReceiveError) {
            return this.getMessageReceiveError;
        }

        switch (this.type) {
            case MessageChatType.imageMessage:
                return (!!this.imageMessage && !!this.imageMessage.caption) ? `📷 Foto (${this.imageMessage.caption})` : "📷 Foto";

            case MessageChatType.stickerMessage:
                return (!!this.stickerMessage && !!this.stickerMessage.caption) ? `📷 Foto (${this.stickerMessage.caption})` : "📷 Foto";

            case MessageChatType.audioMessage:
                return (!!this.audioMessage && !!this.audioMessage.caption) ? `🎤 Mensagem de voz (${this.audioMessage.caption})` : "🎤 Mensagem de voz";

            case MessageChatType.videoMessage:
                return (!!this.videoMessage && !!this.videoMessage.caption) ? `📹 Vídeo (${this.videoMessage.caption})` : "📹 Vídeo";

            case MessageChatType.locationMessage:
                return "📍Localização";

            case MessageChatType.contactMessage:
                return "👤 Contato";

            case MessageChatType.documentMessage:
                return (!!this.documentMessage && !!this.documentMessage.caption) ? `📁 Arquivo (${this.documentMessage.caption})` : "📁 Arquivo";

            case MessageChatType.reactionMessage:
                return `${this.sender?.name} Reagiu com ${this.reactionMessage?.text}`;

            case MessageChatType.systemMessage:
                return this.systemMessage?.conversation ?? '';
            default:
                return this.textMessage?.conversation ?? '';
        }

    }

    public get isFirstPosition(): boolean {
        return this.position === MessagePosition.first;
    }

    public get isLastPosition(): boolean {
        return this.position === MessagePosition.last;
    }

    public get isUniquePosition(): boolean {
        return this.position === MessagePosition.unique;
    }

    public get urlDownload(): string | undefined {
        if (!!this.blobURL) {
            return this.blobURL;
        }
        const types: Record<string, string | undefined> = {
            [MessageChatType.audioMessage]: this.audioMessage?.buffer,
            [MessageChatType.documentMessage]: this.documentMessage?.buffer,
            [MessageChatType.imageMessage]: this.imageMessage?.buffer,
            [MessageChatType.videoMessage]: this.videoMessage?.buffer,
            [MessageChatType.stickerMessage]: this.stickerMessage?.buffer,
        };
        return types[this.type] ?? undefined;
    }

    public get urlThumbnail(): string | undefined {
        const types: Record<string, string | undefined> = {
            [MessageChatType.imageMessage]: this.imageMessage?.jpegThumbnail,
            [MessageChatType.videoMessage]: this.videoMessage?.jpegThumbnail,
        };
        return types[this.type] ?? undefined;
    }

    public get realUrl(): string | undefined {
        return this.showOriginal ? this.urlDownload : !!this.urlThumbnail ? this.urlThumbnail : this.urlDownload;
    }

    public get toReplyEntity(): ReplyEntity | null {
        return new ReplyEntity({
            messageId: this.idExternal ?? this.id,
            quotedConversationType: this.type,
            quotedMessage: this.getQuotedMessage,
            participantId: undefined,
            participant: this.sender ? new SimpleUserInfo({
                id: this.senderId,
                idCompany: this.idCompany,
                name: this.sender.name,
                urlImageProfile: this.sender.urlImageProfile as string | undefined,
                role: this.sender.role,
            }) : undefined,
        });
    }


    public get getQuotedMessage(): MessageMediaBase | null {
        const types: Record<string, any> = {
            [MessageChatType.audioMessage]: this.audioMessage?.toReply ?? null,
            [MessageChatType.documentMessage]: this.documentMessage?.toReply ?? null,
            [MessageChatType.imageMessage]: this.imageMessage?.toReply ?? null,
            [MessageChatType.videoMessage]: this.videoMessage?.toReply ?? null,
            [MessageChatType.stickerMessage]: this.stickerMessage?.toReply ?? null,
            [MessageChatType.locationMessage]: this.locationMessage?.toReply ?? null,
            [MessageChatType.textMessage]: this.textMessage?.toReply ?? null,
        };

        return types[this.type] ?? null;
    }

    public get getText(): string {
        if (this.textMessage) {
            return this.textMessage.conversation;
        }

        if (this.systemMessage) {
            return this.systemMessage.conversation;
        }

        return '';
    }

    public get getFileName(): string {
        if (this.documentMessage && this.documentMessage.fileName) {
            return this.documentMessage.fileName;
        }
        return this.id;
    }

    public get getTextEditable(): string {
        const types: Record<string, any> = {
            [MessageChatType.audioMessage]: this.audioMessage?.caption,
            [MessageChatType.documentMessage]: this.documentMessage?.caption,
            [MessageChatType.imageMessage]: this.imageMessage?.caption,
            [MessageChatType.videoMessage]: this.videoMessage?.caption,
            [MessageChatType.stickerMessage]: this.stickerMessage?.caption,
        };

        return types[this.type] ?? this.getText;
    }

    public get hasSendError() {
        return this.status === MessageStatus.ERROR || this.statusError === StatusMessageError.sendMessageError;
    }

    public getStatus(
        userClient: UserEntity | null,
        currentUserRole: UserRole | null,
        attendant?: UserEntity
    ): MessageBoxStatus | undefined {
        if (this.automatic) {
            return;
        }

        if ('idInternalChat' in this) {
            return this.getGroupChatStatus();
        }

        return this.getOneToOneStatus(userClient, currentUserRole, attendant);
    }

    private getGroupChatStatus(): MessageBoxStatus {
        if (this.users && this.readers && !this.hasSendError) {
            const userIds = Object.keys(this.users).filter(id => id !== this.senderId);
            const allRead = userIds.length > 0 && userIds.every(userId => this.readers![userId] === true);
            if (allRead) {
                return MessageBoxStatus.read;
            }
        }

        if (this.users && this.received && !this.hasSendError) {
            const userIds = Object.keys(this.users).filter(id => id !== this.senderId);
            const allReceived = userIds.length > 0 && userIds.every(userId => this.received![userId] === true);
            if (allReceived) {
                return MessageBoxStatus.received;
            }
        }

        if (this.createdAt && !this.hasSendError) {
            return MessageBoxStatus.sent;
        }

        return MessageBoxStatus.waiting;
    }

    private getOneToOneStatus(
        userClient: UserEntity | null,
        currentUserRole: UserRole | null,
        attendant?: UserEntity
    ): MessageBoxStatus {
        const realId = currentUserRole === UserRole.attendant ? userClient?.id : attendant?.id;

        if (this.readers && realId && this.readers[realId] === true && !this.hasSendError) {
            return MessageBoxStatus.read;
        }

        if (this.received && realId && this.received[realId] === true && !this.hasSendError) {
            return MessageBoxStatus.received;
        }

        if (this.createdAt && ![MessageStatus.ERROR, MessageStatus.PENDING].includes(this.status) && !this.hasSendError) {
            return MessageBoxStatus.sent;
        }

        return MessageBoxStatus.waiting;
    }

    public get getMessageLargeMediaError(): string {
        return `#[ MENSAGEM DE AVISO ]#: Esta é uma mensagem de aviso do Sistema DropDesk, #${this.pushName}# enviou um arquivo com tamanho acima do limite permitido, verifique o arquivo através do seu WhatsApp, limite permitido de 16MB.`;
    }

    public get getMessageViewOnceError(): string {
        return `#[ MENSAGEM DE AVISO ]#: Você recebeu uma mensagem de visualização única, por segurança não aceitamos esse tipo de mensagem, peça ao remetente que envie a mensagem com visualização pública.`;
    }

    public get getMessageReceiveError(): string {
        return `#[ MENSAGEM DE AVISO ]#: Informamos que ocorreu um erro durante o recebimento do arquivo enviado por ${this.pushName}. Solicite que o mesmo envie novamente ou você pode verificar direto no WhatsApp. Agradecemos pela compreensão e pedimos desculpas pelo transtorno.`;
    }

    public get realCreatedAt(): string {
        return this.createdAtLocal ?? this.createdAt;
    }

    public get isLargeMediaError(): boolean {
        return this.statusError === StatusMessageError.largeMediaError;
    }

    public get isViewOnceError(): boolean {
        return this.statusError === StatusMessageError.viewOnceError;
    }

    public get isReceiveError(): boolean {
        return this.statusError === StatusMessageError.receiveMediaError;
    }

    public get getMessageError(): string {
        return this.isLargeMediaError ? this.getMessageLargeMediaError :
            this.isViewOnceError ? this.getMessageViewOnceError : this.getMessageReceiveError;
    }

    public get hasError(): boolean {
        return this.isLargeMediaError || this.isViewOnceError || this.isReceiveError;
    }

    get isFileMessage(): boolean {
        return this.type === MessageChatType.stickerMessage ||
            this.type === MessageChatType.imageMessage ||
            this.type === MessageChatType.videoMessage ||
            this.type === MessageChatType.audioMessage ||
            this.type === MessageChatType.documentMessage;
    }

    public get isValidPreviewMessage(): boolean {
        return this.type === MessageChatType.stickerMessage ||
            this.type === MessageChatType.imageMessage;
    }

    public setUrlDownloadInFileMessage(urlDownload: string): void {
        if (this.audioMessage) this.audioMessage.buffer = urlDownload;
        if (this.documentMessage) this.documentMessage.buffer = urlDownload;
        if (this.imageMessage) this.imageMessage.buffer = urlDownload;
        if (this.videoMessage) this.videoMessage.buffer = urlDownload;
        if (this.stickerMessage) this.stickerMessage.buffer = urlDownload;
        if (this.locationMessage) this.locationMessage.buffer = urlDownload;
    }

    public setEditableText(editText: string): BaseMessageEntity {
        if (this.audioMessage) this.audioMessage.caption = editText;
        if (this.documentMessage) this.documentMessage.caption = editText;
        if (this.imageMessage) this.imageMessage.caption = editText;
        if (this.videoMessage) this.videoMessage.caption = editText;
        if (this.stickerMessage) this.stickerMessage.caption = editText;
        if (this.textMessage) this.textMessage.conversation = editText;
        this.edited = true;

        return this;
    }

    public get isSystemMessage(): boolean {
        return this.type === MessageChatType.systemMessage;
    }

    protected getPathFromBase(basePath: string, mimeType: string, type: string): string {
        const ext = mimeType.split('/')[1];
        return `${basePath}/${type}/${this.id}.${ext}`;
    }

    public getPathToUpload(basePath: string): string {
        const types: Record<string, string> = {
            [MessageChatType.audioMessage]: this.getPathFromBase(basePath, this.audioMessage?.mimetype ?? '', 'audios'),
            [MessageChatType.documentMessage]: this.getPathFromBase(basePath, this.documentMessage?.mimetype ?? '', 'documents'),
            [MessageChatType.imageMessage]: this.getPathFromBase(basePath, this.imageMessage?.mimetype ?? '', 'images'),
            [MessageChatType.videoMessage]: this.getPathFromBase(basePath, this.videoMessage?.mimetype ?? '', 'videos'),
            [MessageChatType.stickerMessage]: this.getPathFromBase(basePath, this.stickerMessage?.mimetype ?? '', 'stickers'),
        };

        return types[this.type] ?? '';
    }

    public subscriptionUploadFile<T extends BaseMessageEntity>(
        this: T,
        remoteDataSource: RemoteStorageDatasource,
        basePath: string
    ): Observable<T> {
        return new Observable<T>((observer: Observer<T>) => {
            const path = this.getPathToUpload(basePath);
            remoteDataSource.uploadFileListener(path, this.uploadOptions!).subscribe({
                next: (response) => {
                    if (response.urlDownload) {
                        this.setUrlDownloadInFileMessage(response.urlDownload);
                    }
                    observer.next((this as any).copyWith({
                        uploadOptions: response,
                    }) as T);
                },
                error: (err: FirebaseStorageError) => {
                    observer.error(err);
                },
                complete: () => {
                    observer.complete();
                }
            });
        });
    }

    public abstract copyWith(data: any): any;

    public static removeDuplicateMessages<T extends BaseMessageEntity>(messages: T[]): T[] {
        return messages.filter((message, index, _messages) => 
            _messages.findIndex(_msg => _msg.id === message.id) === index
        );
    }

    public static orderMessagesByCreatedAt<T extends BaseMessageEntity>(messages: T[]): T[] {
        return messages.sort((a, b) => 
            new Date(b.realCreatedAt).getTime() - new Date(a.realCreatedAt).getTime()
        );
    }

}
